import { motion } from "motion/react";
import { ReactNode, useEffect, useState } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useDeviceCapabilities } from "@/hooks/useDeviceCapabilities";
import { isTestMode } from "@/lib/testMode";

interface Props {
  children: ReactNode;
  className?: string;
  delay?: number;
  blur?: boolean;
  /** Gentle mode: less movement, longer duration — for trust-building & emotional sections */
  gentle?: boolean;
}

// Cinematic easing — smooth deceleration, unified across all reveals
const EASE = [0.22, 0.68, 0.35, 1] as const;

/**
 * Mobile (≤768px): content is visible-by-default. A short, subtle fade
 * still plays as enhancement, but visibility is never gated by an
 * IntersectionObserver — so fast scrolls or low-power devices never see
 * "missing"/half-transparent sections.
 *
 * Desktop (>768px): unchanged cinematic reveal with blur + translate +
 * whileInView trigger.
 */
const AnimatedSection = ({ children, className = "", delay = 0, blur = true, gentle = false }: Props) => {
  const prefersReducedMotion = useReducedMotion();
  const { isLowEndDevice } = useDeviceCapabilities();
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 768px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // Skip motion entirely under reduced-motion, Playwright, or low-end hardware
  // (≤4 cores or ≤4GB RAM). Renders straight into the final state — no fade.
  const skipMotion = prefersReducedMotion || isTestMode() || isLowEndDevice;

  if (skipMotion) {
    return (
      <motion.div initial={false} animate={{ opacity: 1 }} className={className}>
        {children}
      </motion.div>
    );
  }


  // ── Mobile: visible by default, with a quick optional fade-in enhancement.
  // No translate, no blur, no whileInView. Content never appears empty.
  if (isMobile) {
    return (
      <motion.div
        initial={{ opacity: 0.001 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: Math.min(delay, 0.15), ease: "easeOut" }}
        style={{ opacity: 1 }}
        className={className}
      >
        {children}
      </motion.div>
    );
  }

  // Unified motion language: gentle uses less distance, slightly longer duration
  const yOffset = gentle ? 14 : 20;
  const duration = gentle ? 1.2 : 1.0;
  const blurAmount = gentle ? "blur(2px)" : "blur(4px)";

  return (
    <motion.div
      initial={{ opacity: 0, y: yOffset, ...(blur ? { filter: blurAmount } : {}) }}
      whileInView={{ opacity: 1, y: 0, ...(blur ? { filter: "blur(0px)" } : {}) }}
      viewport={{ once: true, margin: gentle ? "-35px" : "-50px" }}
      transition={{ duration, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default AnimatedSection;
